/**
 * CURRENCY UTILITY
 * Centralized currency formatting for tenant-driven display
 * 
 * Supported currencies: USD, GBP, EUR, ZAR, AUD, CAD
 * 
 * Usage:
 *   import { useCurrency } from '../utils/currencyUtils';
 *   const { formatCurrency, currencySymbol } = useCurrency();
 *   formatCurrency(1250000) // Returns "£1,250,000" for GBP tenant
 */

import { useTenant } from '../context/TenantContext';

// Currency configuration
export const CURRENCY_CONFIG = {
  USD: { code: 'USD', symbol: '$', locale: 'en-US', name: 'US Dollar' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', name: 'British Pound' },
  EUR: { code: 'EUR', symbol: '€', locale: 'de-DE', name: 'Euro' },
  ZAR: { code: 'ZAR', symbol: 'R', locale: 'en-ZA', name: 'South African Rand' },
  AUD: { code: 'AUD', symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' },
  CAD: { code: 'CAD', symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
  CHF: { code: 'CHF', symbol: 'CHF', locale: 'de-CH', name: 'Swiss Franc' },
  JPY: { code: 'JPY', symbol: '¥', locale: 'ja-JP', name: 'Japanese Yen' },
  CNY: { code: 'CNY', symbol: '¥', locale: 'zh-CN', name: 'Chinese Yuan' },
  INR: { code: 'INR', symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
  SGD: { code: 'SGD', symbol: 'S$', locale: 'en-SG', name: 'Singapore Dollar' },
  NZD: { code: 'NZD', symbol: 'NZ$', locale: 'en-NZ', name: 'New Zealand Dollar' },
};

// Get currency symbol from code
export const getCurrencySymbol = (code) => {
  return CURRENCY_CONFIG[code]?.symbol || '$';
};

// Get currency config from code
export const getCurrencyConfig = (code) => {
  return CURRENCY_CONFIG[code] || CURRENCY_CONFIG.USD;
};

/**
 * Custom hook for currency formatting
 * Uses tenant context to determine currency
 */
export const useCurrency = () => {
  const { tenant } = useTenant();
  
  // Get currency code from tenant settings, default to USD
  const currencyCode = tenant?.settings?.currency_code || tenant?.currency || 'USD';
  const config = getCurrencyConfig(currencyCode);
  const currencySymbol = config.symbol;
  
  /**
   * Format a value as currency with compact notation (K, M, B)
   * @param {number} value - The numeric value
   * @param {object} options - Formatting options
   * @param {boolean} options.compact - Use K/M/B abbreviations (default: true)
   * @param {number} options.decimals - Number of decimal places (default: 0)
   * @param {boolean} options.showSign - Show +/- sign (default: false)
   */
  const formatCurrency = (value, options = {}) => {
    const num = Number(value) || 0;
    const { compact = true, decimals = 0, showSign = false } = options;
    
    const sign = showSign && num > 0 ? '+' : '';
    const absNum = Math.abs(num);
    const prefix = num < 0 ? '-' : sign;
    
    if (compact) {
      if (absNum >= 1e9) {
        return `${prefix}${currencySymbol}${(absNum / 1e9).toFixed(1)}B`;
      }
      if (absNum >= 1e6) {
        return `${prefix}${currencySymbol}${(absNum / 1e6).toFixed(1)}M`;
      }
      if (absNum >= 1e3) {
        return `${prefix}${currencySymbol}${(absNum / 1e3).toFixed(decimals > 0 ? decimals : 1)}K`;
      }
    }
    
    // Format with thousands separator
    const formatted = absNum.toLocaleString(config.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return `${prefix}${currencySymbol}${formatted}`;
  };
  
  /**
   * Format using Intl.NumberFormat for full control
   * @param {number} value - The numeric value
   * @param {object} options - Intl.NumberFormat options
   */
  const formatCurrencyFull = (value, options = {}) => {
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
        ...options
      }).format(Number(value) || 0);
    } catch (e) {
      // Fallback for unsupported currencies
      return formatCurrency(value, { compact: false });
    }
  };
  
  /**
   * Format large numbers with abbreviations
   * @param {number} value - The numeric value
   */
  const formatCompact = (value) => {
    return formatCurrency(value, { compact: true });
  };
  
  /**
   * Format with full precision (no abbreviations)
   * @param {number} value - The numeric value
   * @param {number} decimals - Decimal places
   */
  const formatPrecise = (value, decimals = 0) => {
    return formatCurrency(value, { compact: false, decimals });
  };
  
  /**
   * Format delta/change values with +/- sign
   * @param {number} value - The numeric value
   */
  const formatDelta = (value) => {
    return formatCurrency(value, { compact: true, showSign: true });
  };
  
  return {
    currencyCode,
    currencySymbol,
    currencyConfig: config,
    formatCurrency,
    formatCurrencyFull,
    formatCompact,
    formatPrecise,
    formatDelta
  };
};

/**
 * Non-hook version for use outside React components
 * @param {number} value - The numeric value
 * @param {string} currencyCode - Currency code (default: USD)
 * @param {object} options - Formatting options
 */
export const formatCurrencyStatic = (value, currencyCode = 'USD', options = {}) => {
  const config = getCurrencyConfig(currencyCode);
  const num = Number(value) || 0;
  const { compact = true, decimals = 0, showSign = false } = options;
  
  const sign = showSign && num > 0 ? '+' : '';
  const absNum = Math.abs(num);
  const prefix = num < 0 ? '-' : sign;
  
  if (compact) {
    if (absNum >= 1e9) return `${prefix}${config.symbol}${(absNum / 1e9).toFixed(1)}B`;
    if (absNum >= 1e6) return `${prefix}${config.symbol}${(absNum / 1e6).toFixed(1)}M`;
    if (absNum >= 1e3) return `${prefix}${config.symbol}${(absNum / 1e3).toFixed(1)}K`;
  }
  
  return `${prefix}${config.symbol}${absNum.toLocaleString(config.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
};

/**
 * Safe currency formatter that handles null/undefined
 * @param {any} value - The value to format
 * @param {string} symbol - Currency symbol
 */
export const safeCurrency = (value, symbol = '$') => {
  const num = Number(value) || 0;
  if (Math.abs(num) >= 1e6) return `${symbol}${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `${symbol}${(num / 1e3).toFixed(1)}K`;
  return `${symbol}${num.toLocaleString()}`;
};

export default useCurrency;
